import { useEffect, useState } from 'react';
import { CSSObject } from '@emotion/react';
import {
  FooterSection as FooterSectionType,
  Link,
} from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { NakedButton } from '@frontend/design-system';
import { useContentComposer } from '../../content-composer/content-composer-provider';
import { useComposerSection } from '../../content-composer/hooks/use-composer-section';
import { SectionBase } from '../../content-composer/section-base/section-base';
import type { ComposerSection } from '../../content-composer/types';
import { EmailSvg } from '../assets/email-svg';
import { FacebookSvg } from '../assets/facebook-svg';
import { GoogleSvg } from '../assets/google-svg';
import { PhoneSvg } from '../assets/phone-svg';
import { useLinks } from '../hooks/use-links';
import { getFrameStyle } from '../utils/get-frame-style';
import { FooterOutput } from './footer-output';
import { FooterSettings } from './footer-settings';
import { FooterTool } from './footer-tool';
import { FooterToolbar } from './footer-toolbar';
import { FooterMode } from './types';

const Pipe = () => (
  <div
    css={{
      width: 1,
      height: 12,
      background: theme.colors.neutral50,
      margin: theme.spacing(0, 2),
    }}
  />
);

const PREVIEW_TEXT = 'preview';

export const FooterSection: ComposerSection<FooterSectionType> = ({
  bold,
  fontType,
  frame,
  italic,
  links,
  textColor,
  textSize,
}) => {
  const { t } = useTranslation('content-composer');
  const { composerMode } = useContentComposer();
  const isEmailComposer = composerMode !== 'feedback';

  const { isSelected, setProps } = useComposerSection<FooterSectionType>();
  const { getAddress, getBusinessName } = useLinks();

  const [mode, setMode] = useState<FooterMode>('default');
  const [selectedLink, setSelectedLink] = useState<Link>();

  const fontStyles: CSSObject = {
    color: textColor,
    fontFamily: fontType,
    fontSize: textSize ? textSize + 'px' : undefined,
    fontStyle: italic ? 'italic' : undefined,
    fontWeight: bold ? theme.font.weight.bold : undefined,
  };

  useEffect(() => {
    if (!isSelected && mode !== 'default') {
      setMode('default');
    }
  }, [isSelected, mode]);

  const handleUnselectClick = () => {
    setSelectedLink(undefined);
    setMode('options');
  };

  const handleLinkUpdate = (link: Link, modeType: FooterMode) => {
    setSelectedLink(link);
    setMode(modeType);
  };

  const alphaNumericRegex = /[^a-z0-9]/gi;
  const createId = (link: Link) =>
    link.linkType + '-' + link.url.replace(alphaNumericRegex, '') + '-' + link.text?.replace(alphaNumericRegex, '');

  return (
    <SectionBase
      disableCopy
      disableDelete={isEmailComposer}
      disableDrag={isEmailComposer}
      className='footer-section'
      css={{
        ...getFrameStyle(frame),
        alignItems: frame?.alignment === 'left' ? 'flex-start' : frame?.alignment === 'right' ? 'flex-end' : 'center',
        flexDirection: 'column',
        minHeight: 'initial',
        rowGap: theme.spacing(2),
      }}
    >
      {links && !!links.length && (
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            columnGap: theme.spacing(1),
          }}
        >
          {links.map((link, index) => (
            <div key={index} css={{ position: 'relative' }}>
              {isSelected && (
                <NakedButton
                  className={createId(link)}
                  css={{
                    position: 'absolute',
                    top: link.linkType === 'custom' ? theme.spacing(-0.5) : 0,
                    left: '50%',
                    transform: 'translate(-50%, -100%)',
                    background: theme.colors.white,
                    borderRadius: theme.borderRadius.small,
                    padding: theme.spacing(0, 0.5),
                    height: theme.spacing(3),
                    width: theme.spacing(3),
                    zIndex: theme.zIndex.middle + 1,
                  }}
                  onClick={() => {
                    if (selectedLink?.url === link.url) setMode('options');
                    setProps((props) => (props.links = links?.filter((l) => l.url !== link.url)));
                  }}
                >
                  <Icon name='x' />
                </NakedButton>
              )}
              {link.linkType === 'custom' ? (
                <NakedButton
                  css={[fontStyles, { textDecoration: 'underline' }]}
                  onClick={() => handleLinkUpdate(link, 'custom')}
                >
                  {link?.text}
                </NakedButton>
              ) : (
                <NakedButton
                  onClick={() =>
                    link.linkType === 'phone' || link.linkType === 'email'
                      ? handleUnselectClick()
                      : handleLinkUpdate(link, 'link')
                  }
                >
                  {link.linkType === 'email' && <EmailSvg fillColor={textColor} />}
                  {link.linkType === 'phone' && <PhoneSvg fillColor={textColor} />}
                  {link.linkType === 'facebook' && <FacebookSvg fillColor={textColor} />}
                  {link.linkType === 'google' && <GoogleSvg fillColor={textColor} />}
                </NakedButton>
              )}
            </div>
          ))}
        </div>
      )}
      <p css={fontStyles}>{getAddress(PREVIEW_TEXT)}</p>
      <p css={fontStyles}>{getBusinessName(PREVIEW_TEXT)}</p>
      {isEmailComposer && (
        <div css={{ display: 'flex', alignItems: 'center' }}>
          {/* TODO: Rename Preference Center to Email Preferences */}
          <NakedButton css={[fontStyles, { textDecoration: 'underline' }]}>{t('Preference Center')}</NakedButton>
          <Pipe />
          <NakedButton css={[fontStyles, { textDecoration: 'underline' }]}>{t('Unsubscribe')}</NakedButton>
        </div>
      )}
      {isSelected && (
        <FooterToolbar mode={mode} selectedLink={selectedLink} setMode={setMode} setSelectedLink={setSelectedLink} />
      )}
    </SectionBase>
  );
};

FooterSection.craft = {
  props: {
    bold: false,
    fontType: 'Arial',
    frame: {
      alignment: 'center',
      backgroundColor: theme.colors.neutral5.toUpperCase(),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
    italic: false,
    links: [],
    sectionType: 'FooterSection',
    textColor: theme.colors.black.toUpperCase(), // TODO: Change this color to match new mocks
    textSize: 16,
  },
  related: {
    settings: FooterSettings,
    output: FooterOutput,
    tool: FooterTool,
  },
};
